import { useCallback, useMemo, useState } from 'react'
import { RatingTypeEnum } from 'types/api/enums'
import { RatingFilter } from 'types/global'
import { InternalState } from './types'
import { useTranslation } from 'react-i18next'

export const useCombinedRatingFilter = ({
  rating,
  minRange,
  maxRange,
}: {
  minRange: number
  maxRange: number
  rating: RatingFilter | null
}) => {
  const { t } = useTranslation('strFilters')
  const [defaultInternalState, deriveInternalState] = useMemo(() => {
    const defaultState: InternalState = {
      type: RatingTypeEnum.ITF,
      confidence: true,
      range: { min: minRange, max: maxRange },
      inputsRange: { min: minRange, max: maxRange },
      error: null,
      canApply: false,
      isGameZoneApplied: false,
    }

    const deriveState = (rating: RatingFilter | null): InternalState => {
      return rating
        ? {
            ...rating,
            inputsRange: { ...rating.range },
            error: null,
            canApply: true,
            isGameZoneApplied: !!rating.gameZone,
          }
        : defaultState
    }

    return [defaultState, deriveState]
  }, [minRange, maxRange])

  const [internalState, setInternalState] = useState<InternalState>(deriveInternalState(rating))
  const { min, max } = internalState.range

  const handleMinInputChange = event => {
    if (!event.target.value) {
      setInternalState(prev => ({ ...prev, inputsRange: { min: null, max }, error: null }))
      return
    }

    if (!max) return

    // labeled as Max rating
    const inputValue = Number(Number(event.target.value).toFixed(1))
    if (inputValue > max) {
      const limitedVal = Math.min(inputValue, maxRange)
      setInternalState(prev => ({
        ...prev,
        range: { min: max, max },
        inputsRange: { min: limitedVal, max },
        error: t('rating.inputError', { min: minRange, max }),
      }))
    } else {
      const limitedVal = Math.max(inputValue, minRange)
      setInternalState(prev => ({
        ...prev,
        range: { min: limitedVal, max },
        inputsRange: { min: limitedVal, max },
        error: null,
      }))
    }
  }

  const handleMaxInputChange = event => {
    if (!event.target.value) {
      setInternalState(prev => ({ ...prev, inputsRange: { min, max: null }, error: null }))
      return
    }

    if (!min) return

    // labeled as Min rating
    const inputValue = Number(Number(event.target.value).toFixed(1))
    if (inputValue < min) {
      const limitedVal = Math.max(inputValue, minRange)
      setInternalState(prev => ({
        ...prev,
        range: { min, max: min },
        inputsRange: { min, max: limitedVal },
        error: t('rating.inputError', { min, max: maxRange }),
      }))
    } else {
      const limitedVal = Math.min(inputValue, maxRange)
      setInternalState(prev => ({
        ...prev,
        range: { min, max: limitedVal },
        inputsRange: { min, max: limitedVal },
        error: null,
      }))
    }
  }

  const reconcileInternalState = useCallback(() => {
    setInternalState(deriveInternalState(rating))
  }, [rating, deriveInternalState])

  const handleInputBlur = () => {
    setInternalState(prev => ({
      ...prev,
      range: { min: prev.inputsRange.min ?? minRange, max: prev.inputsRange.max ?? maxRange },
      inputsRange: { min: prev.inputsRange.min ?? minRange, max: prev.inputsRange.max ?? maxRange },
    }))
  }

  return {
    reconcileInternalState,
    defaultInternalState,
    internalState,
    setInternalState,
    rangeInputHandlers: {
      handleInputBlur,
      handleMinInputChange,
      handleMaxInputChange,
    },
  }
}
